import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import type { Discussion, DiscussionDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AnswerBody from "@/components/AnswerBody";
import SaveOperatorInsightButton from "@/components/operator/SaveOperatorInsightButton";
import { handoffLabel, TILT_LABEL } from "@/components/zespol/labels";

export function ZespolWaiting({
  question,
  themeLabel,
}: {
  question: string;
  themeLabel?: string;
}) {
  return (
    <section className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
      <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand motion-reduce:animate-none" />
      <h2 className="mt-5 text-xl font-semibold tracking-tight text-slate-900">
        Zespół właśnie czyta
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        Bart mówi pierwszy. Strażnik pilnuje Kasi. Szef zamyka tydzień.
      </p>
      {themeLabel && (
        <p className="mt-4 text-xs uppercase tracking-wide text-slate-400">{themeLabel}</p>
      )}
      <p className="mt-3 text-base leading-relaxed text-slate-700">{question}</p>
    </section>
  );
}

export function ZespolLetter({
  row,
  detail,
}: {
  row: Discussion;
  detail?: DiscussionDetail;
}) {
  const [voicesOpen, setVoicesOpen] = useState(false);
  const bart = (detail?.panel || []).find((voice) => voice.id === "bart");
  const straznik = (detail?.panel || []).find((voice) => voice.id === "straznik");
  const szef = (detail?.panel || []).find((voice) => voice.id === "szef");
  const others = (detail?.panel || []).filter(
    (voice) => !["bart", "straznik", "szef"].includes(voice.id),
  );
  const suggestions = detail?.suggestions || [];
  const failed = row.status === "error" || Boolean(detail?.error);

  return (
    <article className="rounded-xl border border-slate-200 bg-white px-5 py-6 shadow-sm sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Zdanie zespołu
          </p>
          <p className="mt-2 text-sm text-slate-500">{row.question || ""}</p>
          {row.schema_label && (
            <p className="mt-1 text-xs text-slate-400">{row.schema_label}</p>
          )}
        </div>
        <SaveOperatorInsightButton
          surface="zespol"
          sourceRef={String(row.id)}
          context={[row.question, row.verdict_preview, detail?.verdict]
            .filter(Boolean)
            .join("\n\n")}
          messages={(detail?.panel || []).map((v) => ({
            role: v.id || "voice",
            content: String(v.text || ""),
          }))}
        />
      </div>

      {failed && (
        <p className="mt-5 text-sm text-destructive" role="alert">
          {detail?.error || "Narada się nie domknęła. Spróbuj jeszcze raz."}
        </p>
      )}

      {detail?.profile_tilt && (
        <p className="mt-5 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          {TILT_LABEL[detail.profile_tilt] || detail.profile_tilt}
        </p>
      )}

      {detail?.verdict && (
        <div className="mt-6">
          <AnswerBody className="text-base text-slate-800" text={detail.verdict} />
          <p className="mt-4 text-sm text-slate-500">
            To list. Role poniżej są dopowiedzeniem, nie lekturą obowiązkową.
          </p>
        </div>
      )}

      {!detail && row.verdict_preview && (
        <AnswerBody className="mt-6 text-base text-slate-800" text={row.verdict_preview} />
      )}

      {suggestions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-slate-900">Co zrobić</h3>
          <p className="mt-1 text-sm text-slate-600">
            Nic nie ląduje samo na tablicy. Ty wybierasz.
          </p>
          <ul className="mt-4 space-y-3">
            {suggestions.map((sug) => (
              <li
                key={sug.title}
                className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{sug.title}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {sug.owner}: {sug.why}
                  </p>
                </div>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="shrink-0 rounded-full border-slate-200 bg-white text-slate-700 hover:bg-white hover:text-slate-900"
                >
                  <Link to={sug.handoff}>
                    {handoffLabel(sug.handoff)}
                    <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(bart || straznik || szef || others.length > 0) && (
        <div className="mt-8 space-y-3">
          {bart && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4">
              <p className="text-sm font-medium text-slate-900">Bart</p>
              <AnswerBody
                className="mt-2 text-sm text-slate-600"
                text={bart.text}
                stripScaffold
              />
            </div>
          )}
          {straznik && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
              <p className="text-sm font-medium text-slate-900">Strażnik</p>
              <AnswerBody className="mt-2 text-sm text-slate-600" text={straznik.text} />
            </div>
          )}
          {szef && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
              <p className="text-sm font-medium text-slate-900">Szef</p>
              <AnswerBody className="mt-2 text-sm text-slate-600" text={szef.text} />
            </div>
          )}
          {others.length > 0 && (
            <>
              <button
                type="button"
                className="mt-4 text-sm font-medium text-slate-500 underline-offset-4 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                onClick={() => setVoicesOpen((cur) => !cur)}
                aria-expanded={voicesOpen}
              >
                {voicesOpen ? "Schowaj role" : "Posłuchaj ról"}
              </button>
              {voicesOpen && (
                <div className="mt-3 space-y-3">
                  {others.map((voice) => (
                    <div
                      key={voice.id}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
                    >
                      <p className="text-sm font-medium text-slate-900">{voice.label}</p>
                      <AnswerBody className="mt-1 text-sm text-slate-600" text={voice.text} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}
